import { initializeApp } from "firebase/app";
import { getAuth, GoogleAuthProvider, OAuthProvider } from "firebase/auth";
import type { Firestore } from "firebase/firestore";

const firebaseConfig = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
  measurementId: process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID,
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);

const appleProvider = new OAuthProvider("apple.com");
appleProvider.addScope("email");
appleProvider.addScope("name");

const googleProvider = new GoogleAuthProvider();
googleProvider.addScope("email");
googleProvider.addScope("profile");

const microsoftProvider = new OAuthProvider("microsoft.com");
microsoftProvider.addScope("email");
microsoftProvider.addScope("profile");

// Lazily load Firestore. `@firebase/firestore` is a large chunk (~140KB) that
// most pages never touch — only the exam logger writes to it — yet it used to
// initialise eagerly here alongside `auth`, so every page that merely needs
// auth paid to download and parse Firestore too. Importing it dynamically
// keeps it out of the initial bundle. The Firebase SDK caches the instance
// per app; we also memoize the import promise so concurrent callers share a
// single load. The type-only import above is erased at compile time and does
// not pull the SDK into the bundle.
let dbPromise: Promise<Firestore> | null = null;
function getDb(): Promise<Firestore> {
  if (!dbPromise) {
    dbPromise = import("firebase/firestore")
      .then(({ getFirestore }) => getFirestore(app))
      .catch((error) => {
        // Don't cache a rejected load. A transient failure (e.g. a chunk
        // fetch that fails offline) would otherwise be pinned in `dbPromise`
        // forever, so every later getDb() call keeps returning the same
        // rejected promise and Firestore never recovers until a full page
        // reload. Clearing it lets the next caller retry the import.
        dbPromise = null;
        throw error;
      });
  }
  return dbPromise;
}

export { auth, getDb, appleProvider, googleProvider, microsoftProvider };
